import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { CreditCard, Wallet, AlertTriangle, TrendingUp, MessageCircle, Plus, Trash2, X, Users, DollarSign, Clock, Receipt, Bell, Pencil, Save, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { toWhatsAppNumber } from "@/lib/utils";

const money = (n) => `$${(n ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const cleanPhone = (p) => (p || "").replace(/\D/g, "");
const custKey = (customer_id, customer_name) => customer_id || `__free__${customer_name}`;

const METHOD_LABELS = { efectivo: "Efectivo", transferencia: "Transferencia", cheque: "Cheque", otro: "Otro" };

export default function Cobranza() {
  const [data, setData] = useState({ kpis: {}, sales: [], by_customer: [] });
  const [tab, setTab] = useState("por_venta"); // por_venta | por_cliente | recordatorios
  const [payFor, setPayFor] = useState(null);
  const [payments, setPayments] = useState([]);
  const [historyFor, setHistoryFor] = useState(null);
  const [toDeletePayment, setToDeletePayment] = useState(null);
  const [selectedSaleIds, setSelectedSaleIds] = useState(new Set());
  const [selectedCustomerKeys, setSelectedCustomerKeys] = useState(new Set());
  const [bulkGroups, setBulkGroups] = useState(null); // null = dialog closed

  const load = async () => {
    const { data } = await api.get("/cobranza/summary");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const openHistory = async (sale) => {
    setHistoryFor(sale);
    const { data } = await api.get(`/sales/${sale.id}/payments`);
    setPayments(data);
  };

  const refreshHistory = async () => {
    if (!historyFor) return;
    const { data } = await api.get(`/sales/${historyFor.id}/payments`);
    setPayments(data);
  };

  const deletePayment = async () => {
    try {
      await api.delete(`/payments/${toDeletePayment.id}`);
      toast.success("Pago eliminado");
      setToDeletePayment(null);
      await refreshHistory();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const { kpis, sales, by_customer } = data;

  const toggleSale = (id) => setSelectedSaleIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleCustomer = (key) => setSelectedCustomerKeys((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const selectAllOverdue = () => setSelectedSaleIds(new Set(sales.filter((s) => s.overdue && cleanPhone(s.customer_phone)).map((s) => s.id)));

  const openBulkDialog = () => {
    let groups = [];
    if (tab === "por_venta") {
      const chosen = sales.filter((s) => selectedSaleIds.has(s.id));
      const byCustomer = {};
      chosen.forEach((s) => {
        const key = custKey(s.customer_id, s.customer_name);
        if (!byCustomer[key]) byCustomer[key] = { key, customer_name: s.customer_name, customer_phone: s.customer_phone, sales: [] };
        byCustomer[key].sales.push(s);
      });
      groups = Object.values(byCustomer);
    } else {
      groups = by_customer
        .filter((c) => selectedCustomerKeys.has(custKey(c.customer_id, c.customer_name)))
        .map((c) => ({
          key: custKey(c.customer_id, c.customer_name),
          customer_name: c.customer_name,
          customer_phone: c.customer_phone,
          sales: sales.filter((s) => custKey(s.customer_id, s.customer_name) === custKey(c.customer_id, c.customer_name)),
        }));
    }
    if (groups.length === 0) return toast.error("Selecciona al menos una factura o cliente con teléfono");
    setBulkGroups(groups);
  };

  const closeBulkDialog = () => {
    setBulkGroups(null);
    setSelectedSaleIds(new Set());
    setSelectedCustomerKeys(new Set());
  };

  const selectionCount = tab === "por_venta" ? selectedSaleIds.size : selectedCustomerKeys.size;

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="cobranza-page">
      <div>
        <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900 dark:text-stone-100">Cobranza</h1>
        <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">Ventas a crédito, pagos parciales y recordatorios por WhatsApp.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPI icon={Wallet} label="Saldo total por cobrar" value={money(kpis.total_balance || 0)} sub={`${kpis.pending_sales_count || 0} ventas pendientes`} tone="blue" />
        <KPI icon={AlertTriangle} label="Saldo vencido" value={money(kpis.overdue_balance || 0)} sub={`${kpis.overdue_sales_count || 0} ventas vencidas`} tone="red" />
        <KPI icon={Clock} label="Por vencer ≤ 7 días" value={money(kpis.due_soon_balance || 0)} sub="Prioridad para llamar" tone="amber" />
        <KPI icon={TrendingUp} label="Cobrado este mes" value={money(kpis.paid_this_month || 0)} sub="Total pagos recibidos" tone="emerald" />
      </div>

      <div className="flex gap-1 border-b border-stone-200 dark:border-stone-800">
        <TabBtn active={tab === "por_venta"} onClick={() => setTab("por_venta")} icon={Receipt} label="Por venta" testId="tab-por-venta" />
        <TabBtn active={tab === "por_cliente"} onClick={() => setTab("por_cliente")} icon={Users} label="Por cliente" testId="tab-por-cliente" />
        <TabBtn active={tab === "recordatorios"} onClick={() => setTab("recordatorios")} icon={Bell} label="Recordatorios automáticos" testId="tab-recordatorios" />
      </div>

      {(tab === "por_venta" || tab === "por_cliente") && (
        <div className="flex items-center gap-3 flex-wrap" data-testid="bulk-selection-bar">
          {tab === "por_venta" && (
            <button onClick={selectAllOverdue} data-testid="select-all-overdue-btn" className="text-xs px-2.5 py-1.5 rounded-md border border-stone-300 dark:border-stone-700 text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors">
              Seleccionar todas las vencidas
            </button>
          )}
          {selectionCount > 0 && (
            <>
              <span className="text-xs text-stone-500 dark:text-stone-400" data-testid="selection-count">{selectionCount} {tab === "por_venta" ? "factura(s)" : "cliente(s)"} seleccionada(s)</span>
              <Button size="sm" onClick={openBulkDialog} data-testid="bulk-whatsapp-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white ml-auto">
                <Send className="w-3.5 h-3.5 mr-1.5" /> Enviar recordatorios ({selectionCount})
              </Button>
              <button onClick={() => { setSelectedSaleIds(new Set()); setSelectedCustomerKeys(new Set()); }} data-testid="clear-selection-btn" className="text-xs text-stone-400 hover:text-stone-700 dark:hover:text-stone-200">
                Cancelar selección
              </button>
            </>
          )}
        </div>
      )}

      {tab === "por_venta" && <SalesTable sales={sales} onPay={setPayFor} onHistory={openHistory} selectedIds={selectedSaleIds} onToggle={toggleSale} />}
      {tab === "por_cliente" && <CustomerTable customers={by_customer} sales={sales} selectedKeys={selectedCustomerKeys} onToggle={toggleCustomer} />}
      {tab === "recordatorios" && <RulesEditor />}

      <BulkWhatsAppDialog groups={bulkGroups} onClose={closeBulkDialog} />

      {/* Payment Modal */}
      <PaymentDialog sale={payFor} onClose={() => setPayFor(null)} onSaved={async () => { setPayFor(null); await load(); }} />

      {/* History Modal */}
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-outfit">Historial de pagos · {historyFor?.folio}</DialogTitle>
            <DialogDescription>{historyFor?.customer_name} · Total {money(historyFor?.total)} · Saldo {money(historyFor?.balance)}</DialogDescription>
          </DialogHeader>
          <div className="border border-stone-200 dark:border-stone-800 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
                <tr>{["Fecha", "Método", "Monto", "Referencia", ""].map((h) => <th key={h} className="px-3 py-2 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-stone-500">Sin pagos registrados.</td></tr>
                ) : payments.map((p) => (
                  <tr key={p.id} className="border-b border-stone-100 dark:border-stone-800">
                    <td className="px-3 py-2 text-sm tabular-nums">{p.payment_date}</td>
                    <td className="px-3 py-2 text-xs"><span className="inline-block px-2 py-0.5 rounded bg-stone-100 dark:bg-stone-800 font-medium">{METHOD_LABELS[p.method] || p.method}</span></td>
                    <td className="px-3 py-2 text-sm tabular-nums font-semibold text-emerald-700 dark:text-emerald-400">{money(p.amount)}</td>
                    <td className="px-3 py-2 text-xs text-stone-600 dark:text-stone-400 font-mono">{p.reference || "—"}</td>
                    <td className="px-3 py-2"><button onClick={() => setToDeletePayment(p)} className="text-stone-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDeletePayment} onOpenChange={(o) => !o && setToDeletePayment(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Revertir pago de {toDeletePayment && money(toDeletePayment.amount)}?</AlertDialogTitle><AlertDialogDescription>El saldo de la venta volverá a incrementarse en ese monto.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={deletePayment} className="bg-red-600 hover:bg-red-700 text-white">Revertir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* -------- SUB COMPONENTS -------- */

const TabBtn = ({ active, onClick, icon: Icon, label, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`px-4 py-2.5 text-sm font-outfit font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${
      active ? "border-[#1E3A8A] text-[#1E3A8A] dark:text-blue-400 dark:border-blue-400" : "border-transparent text-stone-500 hover:text-stone-800 dark:hover:text-stone-200"
    }`}>
    <Icon className="w-4 h-4" strokeWidth={1.75} />
    {label}
  </button>
);

const SalesTable = ({ sales, onPay, onHistory, selectedIds, onToggle }) => (
  <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
          <tr>{["", "Estado", "Folio", "Cliente", "Venta", "Vence", "Total", "Pagado", "Saldo", "Acciones"].map((h, i) => <th key={h || `col-${i}`} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h || <span className="sr-only">Seleccionar</span>}</th>)}</tr>
        </thead>
        <tbody>
          {sales.length === 0 ? (
            <tr><td colSpan={10} className="px-4 py-12 text-center text-sm text-stone-500">Sin ventas a crédito pendientes. ¡Todo cobrado! 🎉</td></tr>
          ) : sales.map((s) => {
            const hasPhone = !!cleanPhone(s.customer_phone);
            return (
            <tr key={s.id} className={`border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50 ${selectedIds?.has(s.id) ? "bg-blue-50/50 dark:bg-blue-950/20" : ""}`} data-testid={`sale-row-${s.folio}`}>
              <td className="px-3 py-3">
                <Checkbox checked={selectedIds?.has(s.id) || false} disabled={!hasPhone} onCheckedChange={() => onToggle(s.id)} data-testid={`sale-select-${s.folio}`} title={hasPhone ? "" : "Sin teléfono registrado"} />
              </td>
              <td className="px-3 py-3">
                {s.overdue ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 dark:bg-red-950/50 text-red-800 dark:text-red-300 text-[10px] uppercase tracking-wider font-outfit font-semibold">
                    <AlertTriangle className="w-3 h-3" /> Vencido {s.days_overdue}d
                  </span>
                ) : (
                  <span className="inline-block px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 text-[10px] uppercase tracking-wider font-outfit font-semibold">
                    Al día
                  </span>
                )}
              </td>
              <td className="px-3 py-3 text-sm font-medium text-[#1E3A8A] dark:text-blue-400">{s.folio}</td>
              <td className="px-3 py-3 text-sm text-stone-800 dark:text-stone-200">
                <div className="font-medium">{s.customer_name}</div>
                {s.customer_phone && <div className="text-xs text-stone-500 font-mono">{s.customer_phone}</div>}
              </td>
              <td className="px-3 py-3 text-sm tabular-nums text-stone-600 dark:text-stone-400">{s.sale_date}</td>
              <td className="px-3 py-3 text-sm tabular-nums">
                <span className={s.overdue ? "text-red-600 dark:text-red-400 font-semibold" : "text-stone-700 dark:text-stone-300"}>{s.due_date}</span>
              </td>
              <td className="px-3 py-3 text-sm tabular-nums text-stone-700 dark:text-stone-300">{money(s.total)}</td>
              <td className="px-3 py-3 text-sm tabular-nums text-emerald-700 dark:text-emerald-400">{money(s.amount_paid)}</td>
              <td className="px-3 py-3 text-sm tabular-nums font-semibold text-amber-700 dark:text-amber-400">{money(s.balance)}</td>
              <td className="px-3 py-3">
                <div className="flex items-center gap-1">
                  <Button size="sm" onClick={() => onPay(s)} data-testid={`pay-btn-${s.folio}`} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white">
                    <DollarSign className="w-3.5 h-3.5 mr-1" /> Pago
                  </Button>
                  <WhatsAppBtn sale={s} />
                  <button onClick={() => onHistory(s)} title="Ver pagos" className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-blue-50 dark:hover:bg-blue-950/40"><Receipt className="w-4 h-4" /></button>
                </div>
              </td>
            </tr>
          );})}
        </tbody>
      </table>
    </div>
  </Card>
);

const CustomerTable = ({ customers, sales, selectedKeys, onToggle }) => (
  <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
          <tr>{["", "Cliente", "Contacto", "Ventas pend.", "Saldo total", "Saldo vencido", "Máx días vencido", "WhatsApp"].map((h, i) => <th key={h || `col-${i}`} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h || <span className="sr-only">Seleccionar</span>}</th>)}</tr>
        </thead>
        <tbody>
          {customers.length === 0 ? (
            <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-stone-500">Sin clientes con saldo pendiente.</td></tr>
          ) : customers.map((c, i) => {
            const key = custKey(c.customer_id, c.customer_name);
            const hasPhone = !!cleanPhone(c.customer_phone);
            return (
            <tr key={i} className={`border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50 ${selectedKeys?.has(key) ? "bg-blue-50/50 dark:bg-blue-950/20" : ""}`}>
              <td className="px-3 py-3">
                <Checkbox checked={selectedKeys?.has(key) || false} disabled={!hasPhone} onCheckedChange={() => onToggle(key)} data-testid={`customer-select-${i}`} title={hasPhone ? "" : "Sin teléfono registrado"} />
              </td>
              <td className="px-3 py-3 text-sm font-medium text-stone-900 dark:text-stone-100">{c.customer_name}</td>
              <td className="px-3 py-3 text-sm text-stone-600 dark:text-stone-400 font-mono">{c.customer_phone || "—"}</td>
              <td className="px-3 py-3 text-sm tabular-nums text-stone-700 dark:text-stone-300">{c.sales_count}</td>
              <td className="px-3 py-3 text-sm tabular-nums font-semibold text-amber-700 dark:text-amber-400">{money(c.total_balance)}</td>
              <td className="px-3 py-3 text-sm tabular-nums">{c.overdue_balance > 0 ? <span className="text-red-600 dark:text-red-400 font-semibold">{money(c.overdue_balance)}</span> : <span className="text-stone-400">—</span>}</td>
              <td className="px-3 py-3 text-sm tabular-nums">{c.max_days_overdue > 0 ? <span className="text-red-600 dark:text-red-400 font-semibold">{c.max_days_overdue} días</span> : <span className="text-stone-400">—</span>}</td>
              <td className="px-3 py-3">
                <CustomerWhatsAppBtn customer={c} sales={sales.filter((s) => custKey(s.customer_id, s.customer_name) === key)} />
              </td>
            </tr>
          );})}
        </tbody>
      </table>
    </div>
  </Card>
);


const WhatsAppBtn = ({ sale }) => {
  const phone = cleanPhone(sale.customer_phone);
  if (!phone) return <span className="w-8 h-8 flex items-center justify-center text-stone-300" title="Sin teléfono"><MessageCircle className="w-4 h-4" /></span>;
  const msg = sale.overdue
    ? `Hola ${sale.customer_name}, un recordatorio amable: la factura ${sale.folio} por ${money(sale.total)} venció el ${sale.due_date} (${sale.days_overdue} días de atraso). Saldo pendiente: ${money(sale.balance)}. ¿Podemos coordinar el pago? Gracias.`
    : `Hola ${sale.customer_name}, recordatorio de tu factura ${sale.folio} por ${money(sale.total)} con vencimiento ${sale.due_date}. Saldo pendiente: ${money(sale.balance)}. ¡Gracias!`;
  const url = `https://wa.me/${toWhatsAppNumber(phone)}?text=${encodeURIComponent(msg)}`;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title="Enviar recordatorio por WhatsApp" data-testid={`wa-btn-${sale.folio}`}
      className="p-1.5 rounded-md text-emerald-600 hover:text-white hover:bg-emerald-600 transition-colors">
      <MessageCircle className="w-4 h-4" />
    </a>
  );
};

const CustomerWhatsAppBtn = ({ customer, sales }) => {
  const phone = cleanPhone(customer.customer_phone);
  if (!phone) return <span className="text-stone-400 text-xs">Sin teléfono</span>;
  const lines = sales.map((s) => `• ${s.folio}: ${money(s.balance)}${s.overdue ? ` (vencida ${s.days_overdue}d)` : ` (vence ${s.due_date})`}`);
  const msg = `Hola ${customer.customer_name}, te comparto tu estado de cuenta:\n\n${lines.join("\n")}\n\n*Saldo total: ${money(customer.total_balance)}*${customer.overdue_balance > 0 ? `\n⚠ Vencido: ${money(customer.overdue_balance)}` : ""}\n\n¿Podemos coordinar el pago? Gracias.`;
  const url = `https://wa.me/${toWhatsAppNumber(phone)}?text=${encodeURIComponent(msg)}`;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium transition-colors">
      <MessageCircle className="w-3.5 h-3.5" />
      Enviar estado
    </a>
  );
};

const BulkWhatsAppDialog = ({ groups, onClose }) => {
  const [sentKeys, setSentKeys] = useState(new Set());

  useEffect(() => { if (groups) setSentKeys(new Set()); }, [groups]);

  if (!groups) return null;

  const send = (g) => {
    const overdueSales = g.sales.filter((s) => s.overdue);
    const totalBalance = g.sales.reduce((sum, s) => sum + (s.balance || 0), 0);
    const overdueBalance = overdueSales.reduce((sum, s) => sum + (s.balance || 0), 0);
    const lines = g.sales.map((s) => `• ${s.folio}: ${money(s.balance)}${s.overdue ? ` (vencida ${s.days_overdue}d)` : ` (vence ${s.due_date})`}`);
    const msg = g.sales.length > 1
      ? `Hola ${g.customer_name}, te comparto tu estado de cuenta:\n\n${lines.join("\n")}\n\n*Saldo total: ${money(totalBalance)}*${overdueBalance > 0 ? `\n⚠ Vencido: ${money(overdueBalance)}` : ""}\n\n¿Podemos coordinar el pago? Gracias.`
      : (g.sales[0].overdue
          ? `Hola ${g.customer_name}, un recordatorio amable: la factura ${g.sales[0].folio} por ${money(g.sales[0].total)} venció el ${g.sales[0].due_date} (${g.sales[0].days_overdue} días de atraso). Saldo pendiente: ${money(g.sales[0].balance)}. ¿Podemos coordinar el pago? Gracias.`
          : `Hola ${g.customer_name}, recordatorio de tu factura ${g.sales[0].folio} por ${money(g.sales[0].total)} con vencimiento ${g.sales[0].due_date}. Saldo pendiente: ${money(g.sales[0].balance)}. ¡Gracias!`);
    window.open(`https://wa.me/${toWhatsAppNumber(g.customer_phone)}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
    setSentKeys((prev) => new Set(prev).add(g.key));
  };

  const sentCount = groups.filter((g) => sentKeys.has(g.key)).length;

  return (
    <Dialog open={!!groups} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bulk-whatsapp-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-600" /> Cobranza masiva por WhatsApp
          </DialogTitle>
          <DialogDescription>
            Haz clic en "Enviar" por cada cliente para abrir WhatsApp con el mensaje ya redactado · <span data-testid="bulk-sent-progress">{sentCount}/{groups.length} enviados</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {groups.map((g) => {
            const sent = sentKeys.has(g.key);
            const totalBalance = g.sales.reduce((sum, s) => sum + (s.balance || 0), 0);
            return (
              <div key={g.key} data-testid={`bulk-group-${g.key}`} className={`flex items-center gap-3 p-3 rounded-md border ${sent ? "border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-900" : "border-stone-200 dark:border-stone-800"}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-stone-900 dark:text-stone-100 truncate">{g.customer_name}</div>
                  <div className="text-xs text-stone-500 dark:text-stone-400">{g.sales.length} factura(s) · Saldo {money(totalBalance)} · {g.customer_phone}</div>
                </div>
                <Button
                  size="sm"
                  onClick={() => send(g)}
                  data-testid={`bulk-send-${g.key}`}
                  className={sent ? "bg-stone-200 dark:bg-stone-800 text-stone-600 dark:text-stone-300 hover:bg-stone-300" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                >
                  {sent ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Reenviar</> : <><Send className="w-3.5 h-3.5 mr-1.5" /> Enviar</>}
                </Button>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end pt-2 border-t border-stone-200 dark:border-stone-800">
          <Button variant="outline" onClick={onClose} data-testid="bulk-dialog-close-btn">Cerrar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


const PaymentDialog = ({ sale, onClose, onSaved }) => {
  const [form, setForm] = useState({ amount: "", method: "transferencia", payment_date: new Date().toISOString().slice(0, 10), reference: "", notes: "", account_id: "" });
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    if (sale) {
      setForm({ amount: sale.balance?.toString() || "", method: "transferencia", payment_date: new Date().toISOString().slice(0, 10), reference: "", notes: "", account_id: "" });
      api.get("/treasury/accounts").then((r) => setAccounts(r.data.filter((a) => a.active !== false))).catch(() => {});
    }
  }, [sale]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Monto inválido");
    if (amt > (sale.balance + 0.009)) return toast.error(`El monto supera el saldo (${money(sale.balance)})`);
    try {
      const payload = { ...form, amount: amt, account_id: form.account_id || null };
      const { data } = await api.post(`/sales/${sale.id}/payments`, payload);
      const suf = form.account_id ? " · movimiento a tesorería registrado" : "";
      toast.success(`Pago registrado. Nuevo saldo: ${money(data.balance)}${suf}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar pago");
    }
  };

  return (
    <Dialog open={!!sale} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-outfit">Registrar pago · {sale?.folio}</DialogTitle>
          <DialogDescription>
            {sale?.customer_name} · Total {money(sale?.total)} · Ya pagado {money(sale?.amount_paid)} · <span className="font-semibold text-amber-700 dark:text-amber-400">Saldo {money(sale?.balance)}</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Monto *</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" data-testid="payment-amount" placeholder="0.00" autoFocus />
              <div className="flex gap-1 mt-1.5">
                <button type="button" onClick={() => setForm({ ...form, amount: sale?.balance?.toString() })} className="text-[10px] px-2 py-0.5 rounded bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-700 dark:text-stone-300">Total ({money(sale?.balance)})</button>
                <button type="button" onClick={() => setForm({ ...form, amount: (sale?.balance / 2)?.toFixed(2) })} className="text-[10px] px-2 py-0.5 rounded bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-700 dark:text-stone-300">50%</button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Método</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Fecha</Label>
              <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Referencia</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5" placeholder="Núm. transferencia, folio, etc." />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Cuenta destino (Tesorería, opcional)</Label>
            <Select value={form.account_id || "_none"} onValueChange={(v) => setForm({ ...form, account_id: v === "_none" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="payment-account"><SelectValue placeholder="Sin registrar en tesorería" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— No registrar en tesorería —</SelectItem>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency} · {money(a.balance || 0)}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-stone-500 mt-1">Si eliges cuenta, el ingreso se refleja automáticamente en Tesorería.</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Notas</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5" />
          </div>
          <div className="flex gap-2 justify-end pt-2 border-t border-stone-200 dark:border-stone-800">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" data-testid="payment-submit-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">Registrar pago</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const TONES = {
  blue: "bg-[#1E3A8A]/10 text-[#1E3A8A] dark:text-blue-400",
  amber: "bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400",
  emerald: "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400",
  red: "bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400",
};

const KPI = ({ icon: Icon, label, value, sub, tone = "blue" }) => (
  <Card className="p-5 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500 dark:text-stone-400">{label}</div>
        <div className="mt-2 font-outfit font-semibold text-2xl text-stone-900 dark:text-stone-100 tabular-nums">{value}</div>
        {sub && <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">{sub}</div>}
      </div>
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${TONES[tone]}`}>
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
    </div>
  </Card>
);

/* -------- RULES EDITOR -------- */

const RulesEditor = () => {
  const [rules, setRules] = useState([]);
  const [running, setRunning] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name, days_offset, template, active }
  const [showNew, setShowNew] = useState(false);

  const load = () => api.get("/reminder-rules").then((r) => setRules(r.data));
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/notifications/generate");
      toast.success(`Escaneo completo · ${data.created} nuevas · revisadas ${data.checked} combinaciones`);
    } catch { toast.error("Error"); }
    finally { setRunning(false); }
  };

  const saveRule = async (r) => {
    try {
      const payload = { name: r.name, days_offset: parseInt(r.days_offset), template: r.template, active: !!r.active };
      if (!payload.name.trim()) return toast.error("Nombre requerido");
      if (isNaN(payload.days_offset)) return toast.error("Días inválidos");
      if (r.id) await api.put(`/reminder-rules/${r.id}`, payload);
      else await api.post("/reminder-rules", payload);
      toast.success(r.id ? "Regla actualizada" : "Regla creada");
      setEditing(null);
      setShowNew(false);
      await load();
      await runNow(); // Regenerate notifications with new rule
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const del = async (rid) => {
    if (!window.confirm("¿Eliminar esta regla? Las notificaciones ya generadas quedarán pero no se crearán nuevas.")) return;
    try {
      await api.delete(`/reminder-rules/${rid}`);
      toast.success("Regla eliminada");
      await load();
    } catch { toast.error("Error"); }
  };

  const toggleActive = async (r) => {
    try {
      await api.put(`/reminder-rules/${r.id}`, { name: r.name, days_offset: r.days_offset, template: r.template, active: !r.active });
      await load();
      toast.success(!r.active ? "Regla activada" : "Regla pausada");
    } catch { toast.error("Error"); }
  };

  const describe = (d) => {
    if (d < 0) return `${Math.abs(d)} día(s) ANTES del vencimiento`;
    if (d === 0) return "El día del vencimiento";
    return `${d} día(s) DESPUÉS del vencimiento`;
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 border-blue-200 dark:border-blue-900 bg-blue-50/50 dark:bg-blue-950/20">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[#1E3A8A] text-white flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <div className="font-outfit font-semibold text-stone-900 dark:text-stone-100">Motor de recordatorios automáticos</div>
            <div className="text-sm text-stone-600 dark:text-stone-400 mt-1">
              El sistema revisa <b>cada hora</b> tus ventas a crédito y genera alertas según las reglas configuradas.
              Al hacer clic en <b>"Enviar por WhatsApp"</b> desde la campana <span className="inline-flex items-center gap-0.5"><Bell className="w-3 h-3 inline" /></span>, se abre WhatsApp con un mensaje pre-armado con los datos del cliente y la factura.
            </div>
            <div className="mt-3">
              <Button size="sm" onClick={runNow} disabled={running} data-testid="rules-run-now" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
                {running ? "Ejecutando…" : "Ejecutar escaneo ahora"}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center gap-2">
          <h3 className="font-outfit font-semibold text-stone-900 dark:text-stone-100">Reglas configuradas</h3>
          <span className="text-xs text-stone-500 ml-auto">{rules.length} reglas · {rules.filter((r) => r.active).length} activas</span>
          {!showNew && !editing && (
            <Button size="sm" onClick={() => setShowNew(true)} data-testid="new-rule-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
              <Plus className="w-3.5 h-3.5 mr-1" /> Nueva regla
            </Button>
          )}
        </div>

        {showNew && <RuleForm onSave={saveRule} onCancel={() => setShowNew(false)} />}

        <div className="divide-y divide-stone-100 dark:divide-stone-800">
          {rules.length === 0 && !showNew ? (
            <div className="px-4 py-8 text-center text-sm text-stone-500">Sin reglas configuradas.</div>
          ) : rules.map((r) => (
            editing?.id === r.id ? (
              <div key={r.id} className="p-5 bg-stone-50 dark:bg-stone-900/50">
                <RuleForm rule={editing} onSave={saveRule} onCancel={() => setEditing(null)} />
              </div>
            ) : (
              <div key={r.id} className="px-5 py-3.5 flex items-start gap-4 hover:bg-stone-50 dark:hover:bg-stone-900/50" data-testid={`rule-row-${r.id}`}>
                <button onClick={() => toggleActive(r)} data-testid={`rule-toggle-${r.id}`} className={`mt-1 w-9 h-5 rounded-full transition-colors ${r.active ? "bg-emerald-500" : "bg-stone-300 dark:bg-stone-700"} relative flex-shrink-0`}>
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${r.active ? "left-4" : "left-0.5"}`} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-outfit font-semibold text-sm text-stone-900 dark:text-stone-100">{r.name}</span>
                    <span className={`text-[10px] uppercase tracking-wider font-outfit font-semibold px-1.5 py-0.5 rounded ${r.days_offset < 0 ? "bg-blue-100 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300" : r.days_offset === 0 ? "bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300" : "bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300"}`}>
                      {r.days_offset > 0 ? `+${r.days_offset}d` : `${r.days_offset}d`}
                    </span>
                    {!r.active && <span className="text-[10px] uppercase tracking-wider text-stone-400 font-outfit">Pausada</span>}
                  </div>
                  <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">{describe(r.days_offset)}</div>
                  <div className="text-xs text-stone-600 dark:text-stone-400 mt-1.5 italic line-clamp-2">"{r.template}"</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditing(r)} title="Editar" className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-blue-50 dark:hover:bg-blue-950/40"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(r.id)} title="Eliminar" className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            )
          ))}
        </div>
      </Card>
    </div>
  );
};

const emptyRule = { name: "", days_offset: 0, template: "Hola {customer}, recordatorio de tu factura {folio} por {total} con saldo pendiente {balance} (vence {due_date}, {days} día(s)). ¿Podemos coordinar el pago?", active: true };

const RuleForm = ({ rule, onSave, onCancel }) => {
  const [form, setForm] = useState(rule || emptyRule);
  useEffect(() => { setForm(rule || emptyRule); }, [rule]);
  return (
    <div className="p-5 bg-stone-50 dark:bg-stone-900/50 border-b border-stone-200 dark:border-stone-800 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Nombre de la regla *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="Ej. Cortesía 3 días antes" data-testid="rule-name" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Días *</Label>
          <Input type="number" value={form.days_offset} onChange={(e) => setForm({ ...form, days_offset: e.target.value })} className="mt-1 tabular-nums" placeholder="-3, 0, 7…" data-testid="rule-days" />
          <div className="text-[10px] text-stone-500 mt-1">Negativo = antes · 0 = día de vencimiento · Positivo = después</div>
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Mensaje (con placeholders)</Label>
        <Textarea value={form.template} onChange={(e) => setForm({ ...form, template: e.target.value })} className="mt-1 font-mono text-xs" rows={4} data-testid="rule-template" />
        <div className="text-[10px] text-stone-500 mt-1">Placeholders: {"{customer}, {folio}, {total}, {balance}, {due_date}, {days}"}</div>
      </div>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-xs text-stone-700 dark:text-stone-300 cursor-pointer">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          Regla activa (se procesa cada hora)
        </label>
        <div className="ml-auto flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button type="button" size="sm" onClick={() => onSave(form)} data-testid="rule-save-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
            <Save className="w-3.5 h-3.5 mr-1" /> Guardar
          </Button>
        </div>
      </div>
    </div>
  );
};
